import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  getAnalytics,
  isSupported,
  logEvent,
  setUserId,
  setUserProperties,
  type Analytics,
} from "firebase/analytics";

type AnalyticsParams = Record<string, string | number | boolean | null>;
type UserProperties = Record<string, string | number | boolean | null>;

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const hasFirebaseConfig = Boolean(
  firebaseConfig.apiKey &&
    firebaseConfig.projectId &&
    firebaseConfig.appId &&
    firebaseConfig.measurementId,
);

let app: FirebaseApp | null = null;
let analyticsPromise: Promise<Analytics | null> | null = null;

function getFirebaseApp() {
  if (!hasFirebaseConfig || typeof window === "undefined") {
    return null;
  }

  if (!app) {
    app = initializeApp(firebaseConfig);
  }

  return app;
}

async function getFirebaseAnalytics() {
  if (!hasFirebaseConfig || typeof window === "undefined") {
    return null;
  }

  if (!analyticsPromise) {
    analyticsPromise = isSupported()
      .then((supported) => {
        const firebaseApp = getFirebaseApp();
        return supported && firebaseApp ? getAnalytics(firebaseApp) : null;
      })
      .catch(() => null);
  }

  return analyticsPromise;
}

export function trackEvent(eventName: string, params?: AnalyticsParams) {
  void getFirebaseAnalytics().then((analytics) => {
    if (!analytics) return;
    logEvent(analytics, eventName, params);
  });
}

export function trackPageView(pathname: string, search = "") {
  if (typeof window === "undefined") return;

  const sanitizedLocation = `${window.location.origin}${pathname}`;
  trackEvent("page_view", {
    page_location: sanitizedLocation,
    page_path: pathname,
    page_title: document.title,
    has_query: Boolean(search),
  });
}

export function identifyAnalyticsUser(
  userId: string,
  properties?: UserProperties,
) {
  void getFirebaseAnalytics().then((analytics) => {
    if (!analytics) return;
    setUserId(analytics, userId);
    if (properties) {
      setUserProperties(analytics, properties);
    }
  });
}

export function clearAnalyticsUser() {
  void getFirebaseAnalytics().then((analytics) => {
    if (!analytics) return;
    setUserId(analytics, null);
  });
}
